import { useCallback, useMemo, useState } from 'react'

import { useJourneysListView } from '@/features/journeys/hooks/useJourneysListView'
import { useMedalsCollection } from '@/features/medals/hooks/useMedalsCollection'
import { useRankingData } from '@/features/ranking/hooks/useRankingData'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useGamificationPages } from '@/shared/lib/api/gamification-pages'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import type { GamificationPage } from '@/types/gamificationPage'

import {
  buildActiveModuleTypes,
  filterByActiveModules
} from './model/activeModules'
import { buildMedalsGuidance } from './model/buildMedalsGuidance'
import { buildMissionsGuidance } from './model/buildMissionsGuidance'
import { buildRankingGuidance } from './model/buildRankingGuidance'
import { GUIDANCE_COPY } from './model/copy'
import {
  medalsEmptiness,
  missionsEmptiness,
  rankingEmptiness,
  type BlockEmptiness
} from './model/emptiness'
import { useHomeView } from '../../hooks/useHomeView'

import type { GuidanceAction, GuidanceBlockKind } from './model/types'

export interface BlockGuidance {
  emptiness: BlockEmptiness
  actions: GuidanceAction[]
  /** Oferecer a porta? Falso também derruba o sheet. */
  hasGuidance: boolean
  doorLabel: string
  sheetTitle: string
  open: boolean
  onOpen: () => void
  onClose: () => void
}

/**
 * Junta estado do bloco, regras configuradas e filtro de módulo numa coisa só,
 * e é o **único** ponto do domínio que lê hook.
 *
 * `emptiness.ts` e os `build*Guidance` são funções puras justamente porque a
 * leitura de dado mora aqui — é o que os deixa testáveis sem RTL.
 *
 * Todos os hooks de dado são chamados sempre, independentemente do bloco: hook
 * não pode ser condicional.
 *
 * **Isso tem custo, e não é zero.** Os quatro blocos não coexistem
 * necessariamente — `useHomeBlocks` só renderiza os que o produtor posicionou —
 * e `useRecords` roda com `refetchInterval: 60000` e
 * `refetchIntervalInBackground: true`. Uma home só com o bloco de missões passa
 * a repetir `/medals`, `/insights/dashboard` e `objectives` a cada minuto sem
 * desenhar nada a partir deles. Resolver exige um hook por bloco ou `enabled`
 * condicional; está registrado em `findings.md` como decisão pendente, não como
 * custo inexistente.
 */
export const useBlockGuidance = (kind: GuidanceBlockKind): BlockGuidance => {
  const [open, setOpen] = useState(false)

  const { itemsKnown } = useNavigation()
  const { records: pages, isSuccess: pagesLoaded } =
    useGamificationPages() as {
      records: GamificationPage[]
      isSuccess: boolean
    }

  const getActiveProductData = useGamificationDataStore(
    state => state.getActiveProductData
  )
  const dataByProductId = useGamificationDataStore(state => state.dataByProductId)
  const activeDigitalProductId = useGamificationDataStore(
    state => state.activeDigitalProductId
  )

  const medals = useMedalsCollection()
  const ranking = useRankingData()
  const home = useHomeView()
  const journeys = useJourneysListView()

  const snapshot = useMemo(
    () => getActiveProductData(),
    // `getActiveProductData` é estável e lê o estado no momento da chamada — as
    // dependências reais são as duas abaixo. Mesmo contrato de `useHomeBlocks`.
    [getActiveProductData, dataByProductId, activeDigitalProductId]
  )

  const emptiness = useMemo((): BlockEmptiness => {
    switch (kind) {
      case 'medals':
        return medalsEmptiness({
          total: medals.total,
          unlockedCount: medals.unlockedCount
        })
      case 'ranking':
        return rankingEmptiness({ isEmpty: ranking.isEmpty })
      case 'missions':
        return missionsEmptiness({
          missionsTotal: home.missionsTotal,
          pendingMissionsCount: home.pendingMissionsCount
        })
    }
  }, [
    kind,
    medals.total,
    medals.unlockedCount,
    ranking.isEmpty,
    home.missionsTotal,
    home.pendingMissionsCount
  ])

  const actions = useMemo((): GuidanceAction[] => {
    const built = (() => {
      switch (kind) {
        case 'medals':
          return buildMedalsGuidance(medals.medals, {
            journeys: snapshot.journeys ?? [],
            missions: snapshot.missions ?? []
          })
        case 'ranking':
          return buildRankingGuidance(
            {
              missions: snapshot.missions ?? [],
              quizzes: snapshot.quizzes ?? [],
              documents: snapshot.documents ?? []
            },
            journeys.allItems.map(item => item._totalPointReward ?? 0)
          )
        case 'missions':
          return buildMissionsGuidance(home.missionsTotal)
      }
    })()

    // `pagesLoaded` e não o tamanho do conjunto: menu só com páginas avulsas,
    // ou com todas as de módulo inativas, carrega `pages` e produz conjunto
    // vazio — filtrar por tamanho deixaria passar tudo justamente ali.
    return filterByActiveModules(
      built,
      buildActiveModuleTypes(pages),
      pagesLoaded
    )
  }, [
    kind,
    medals.medals,
    snapshot,
    journeys.allItems,
    home.missionsTotal,
    pages,
    pagesLoaded
  ])

  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  return {
    emptiness,
    actions,
    // As duas metades são obrigatórias. Sem `itemsKnown`, a porta levaria a um
    // destino ainda não confirmado; sem ações, abriria um sheet vazio.
    hasGuidance: itemsKnown && actions.length > 0,
    doorLabel: GUIDANCE_COPY[kind].door,
    sheetTitle: GUIDANCE_COPY[kind].sheet,
    open,
    onOpen,
    onClose
  }
}
